import { useQuery } from '@tanstack/react-query';
import { QUERY_KEYS } from './constants';
import {
  deleteTool,
  deleteToolVersion,
  deleteToolVersionFile,
  getTool,
  getToolVerifiers,
  patchUpdateTool,
  postAddToolVersionFile,
  postCreateToolVersion,
  postToggleToolVersion,
} from './adminToolsApi';
import useOptimisticMutation from './useOptimisticMutation';
import { UpdateToolByAdminModel } from 'types/admin-tools/UpdateToolByAdminModel';
import { PatchedToolForAdminModel } from 'types/admin-tools/PatchedToolForAdminModel';
import PcrService from 'services/PcrService';
import { ToggleToolVersionByAdminModel } from 'types/admin-tools/ToggleToolVersionByAdminModel';
import { AddToolVersionFileByAdminModel } from 'types/admin-tools/AddToolVersionFileByAdminModel';
import { AdminCompanySearchRequest } from 'types/admin-search-companies/AdminCompanySearchRequest';
import { postSearchCompanies } from './adminApi';
import AdminSearchCompaniesQueryHelper from 'pages/admin/companies/search/AdminSearchCompaniesQueryHelper';

export const useTool = (toolId: string) =>
  useQuery({
    queryKey: [QUERY_KEYS.ADMIN_TOOL, toolId],
    queryFn: ({ signal }) => getTool(toolId, { signal }) as Promise<PatchedToolForAdminModel>,
    throwOnError: true,
    retry: 0,
  });

export const useToolVerifiers = () =>
  useQuery({
    queryKey: [QUERY_KEYS.ADMIN_TOOL_VERIFIERS],
    queryFn: ({ signal }) => getToolVerifiers({ signal }),
    throwOnError: true,
  });

export const useToolCpcrsOfMainPcr = (pcrId: string | undefined | null) =>
  useQuery({
    queryKey: [QUERY_KEYS.ADMIN_TOOL_CPCR_LIST, pcrId],
    queryFn: () =>
      PcrService.findPcr({
        pcrType: 'CPCR',
        mainPCRId: pcrId,
        isDisabled: false,
      }),
    refetchOnWindowFocus: false,
    enabled: !!pcrId,
  });

export const useToolCompaniesSearch = (params: AdminCompanySearchRequest) =>
  useQuery({
    queryKey: [QUERY_KEYS.ADMIN_TOOL_COMPANIES_LIST, AdminSearchCompaniesQueryHelper.stringify(params)],
    queryFn: () => postSearchCompanies(params),
    placeholderData: (prev) => prev,
    retry: false,
  });

export const useUpdateToolMutation = (toolId: string) =>
  useOptimisticMutation<UpdateToolByAdminModel, PatchedToolForAdminModel>({
    mutationFn: (data) => patchUpdateTool(toolId, data),
    queryKey: [QUERY_KEYS.ADMIN_TOOL, toolId],
    applyOptimisticChanges(queryData, args) {
      if (args.versionOrd === null) {
        return { ...queryData, [args.propertyName as keyof typeof queryData]: args.value };
      }
      return {
        ...queryData,
        versions: queryData.versions.map((v) =>
          v !== 'skeleton' && v.ord === args.versionOrd ? { ...v, [args.propertyName]: args.value } : v
        ),
      };
    },
    serialScopeId: toolId,
  });

export const useAddToolVersionMutation = (toolId: string) =>
  useOptimisticMutation<void, PatchedToolForAdminModel>({
    mutationFn: () => postCreateToolVersion(toolId),
    queryKey: [QUERY_KEYS.ADMIN_TOOL, toolId],
    applyOptimisticChanges(queryData) {
      return { ...queryData, versions: ['skeleton', ...queryData.versions] };
    },
    serialScopeId: toolId,
    onAllSettled({ queryClient }) {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TOOL_STATS, toolId], exact: false });
    },
  });

export const useToggleToolVersionMutation = (toolId: string, versionOrd: number) =>
  useOptimisticMutation<ToggleToolVersionByAdminModel, PatchedToolForAdminModel>({
    mutationFn: (data) => postToggleToolVersion(toolId, versionOrd, data),
    queryKey: [QUERY_KEYS.ADMIN_TOOL, toolId],
    applyOptimisticChanges() {
      return undefined;
    },
    serialScopeId: toolId,
  });

export const useAddToolVersionFileMutation = (toolId: string, versionOrd: number) =>
  useOptimisticMutation<AddToolVersionFileByAdminModel, PatchedToolForAdminModel>({
    mutationFn: (data) => postAddToolVersionFile(toolId, versionOrd, data),
    queryKey: [QUERY_KEYS.ADMIN_TOOL, toolId],
    applyOptimisticChanges() {
      return undefined;
    },
    serialScopeId: toolId,
  });

export const useDeleteToolMutation = (toolId: string) =>
  useOptimisticMutation<void, PatchedToolForAdminModel>({
    mutationFn: () => deleteTool(toolId),
    queryKey: [QUERY_KEYS.ADMIN_TOOL, toolId],
    applyOptimisticChanges() {
      return undefined;
    },
    serialScopeId: toolId,
  });

export const useDeleteToolVersionMutation = (toolId: string) =>
  useOptimisticMutation<number, PatchedToolForAdminModel>({
    mutationFn: (versionOrd: number) => deleteToolVersion(toolId, versionOrd),
    queryKey: [QUERY_KEYS.ADMIN_TOOL, toolId],
    applyOptimisticChanges(queryData, versionOrd) {
      return { ...queryData, versions: queryData.versions.filter((v) => v === 'skeleton' || v.ord !== versionOrd) };
    },
    serialScopeId: toolId,
  });

export const useDeleteToolVersionFileMutation = (toolId: string, versionOrd: number) =>
  useOptimisticMutation<string, PatchedToolForAdminModel>({
    mutationFn: (fileId: string) => deleteToolVersionFile(toolId, versionOrd, fileId),
    queryKey: [QUERY_KEYS.ADMIN_TOOL, toolId],
    applyOptimisticChanges(queryData, fileId) {
      return {
        ...queryData,
        versions: queryData.versions.map((v) =>
          v !== 'skeleton' && v.ord === versionOrd
            ? {
                ...v,
                files: v.files.filter((f) => f.id !== fileId),
              }
            : v
        ),
      };
    },
    serialScopeId: toolId,
  });
